/**
 * Route Utilities
 * Utilities for route classification and validation
 */

/**
 * Check if a route is public (no authentication required)
 * 
 * Uses three-tier matching for security:
 * 1. Exact match set
 * 2. Prefix match set (with leading slash boundary)
 * 3. Regex match set (anchored with ^ and $)
 * 
 * IMPORTANT: This is intentionally non-reactive by design.
 * Context/token changes require full page reload (not hot-swapped).
 * 
 * @param path - The pathname to check (e.g., '/login', '/dashboard')
 * @returns True if the route is public, false if protected
 */
export function isPublicRoute(path: string): boolean {
  // Normalize path (ensure leading slash, strip trailing slashes except root)
  let normalizedPath = path.startsWith('/') ? path : `/${path}`
  normalizedPath = normalizedPath.replace(/\/+$/, '') || '/'
  
  // Tier 1: Exact match set
  const EXACT_PUBLIC = new Set([
    '/',
    '/features',
    '/login',
    '/register',
    '/onboarding',
    '/select-context',
    '/welcome',
    '/pricing',
    '/contact',
    '/terms-and-conditions',
    '/privacy-policy',
    '/cancellation-and-refund',
    '/logout',
  ])
  
  if (EXACT_PUBLIC.has(normalizedPath)) {
    return true
  }
  
  // Tier 2: Prefix match set (with leading slash boundary check)
  // CRITICAL: Must use startsWith with full prefix to prevent false matches
  // Example: '/dashboard/onboarding' should NOT match '/onboarding/'
  const PREFIX_PUBLIC = [
    '/onboarding/',
    '/verifyplatformuseraccount',
    '/accept-platform-invitation',
  ]
  
  for (const prefix of PREFIX_PUBLIC) {
    if (normalizedPath.startsWith(prefix)) {
      return true
    }
  }

  // Tier 2b: Public invite/profile onboarding (e.g. /orgSlug/branchSlug/onboarding/complete-profile).
  // Must NOT match authenticated HR admin routes (/hr/onboarding/…).
  if (normalizedPath.includes('/onboarding/') && !normalizedPath.includes('/hr/onboarding/')) {
    return true
  }
  
  // Tier 3: Regex match set (anchored with ^ and $)
  // Single slug patterns: /{branchSlug}/verifybranchuseraccount
  // Double slug patterns: /{orgSlug}/{branchSlug}/verifybranchuseraccount
  const REGEX_PUBLIC = [
    /^\/[^/]+\/verifybranchuseraccount$/,           // Single slug: verifybranchuseraccount
    /^\/[^/]+\/accept-invitation$/,                 // Single slug: accept-invitation
    /^\/[^/]+\/[^/]+\/verifybranchuseraccount$/,    // Double slug: verifybranchuseraccount
    /^\/[^/]+\/[^/]+\/accept-invitation$/,          // Double slug: accept-invitation
  ]
  
  for (const regex of REGEX_PUBLIC) {
    if (regex.test(normalizedPath)) {
      return true
    }
  }
  
  // Not a public route
  return false
}

/** Marketing landing pages that use the public platform theme (light mode). */
const MARKETING_LANDING_ROUTES = new Set(['/', '/features', '/pricing', '/contact'])

/** Public pre-auth flows that share platform light theme (public API + optional ui-preferences). */
const PUBLIC_PLATFORM_THEME_ROUTES = new Set([...MARKETING_LANDING_ROUTES, '/onboarding'])

function normalizeRoutePath(path: string): string {
  let normalizedPath = path.startsWith('/') ? path : `/${path}`
  normalizedPath = normalizedPath.replace(/\/+$/, '') || '/'
  return normalizedPath
}

/**
 * Home / Features / Pricing / Contact — themed from GET /platform/themes/public light config.
 * Login and other public shells stay on document defaults (no user theme).
 */
export function isMarketingLandingRoute(path: string): boolean {
  return MARKETING_LANDING_ROUTES.has(normalizeRoutePath(path))
}

/**
 * Marketing pages + public onboarding wizard — apply platform light theme tokens.
 */
export function usesPublicPlatformTheme(path: string): boolean {
  return PUBLIC_PLATFORM_THEME_ROUTES.has(normalizeRoutePath(path))
}

